import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Plus, Edit, Trash2, Save, Settings, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const IQCEngine = ({ user, onLogout }) => {
  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingParam, setEditingParam] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/iqc/templates/Android`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplate(response.data);
      setCategories(response.data.categories || []);
      
      if (response.data.categories && response.data.categories.length > 0) {
        setSelectedCategory(response.data.categories[0]);
        setParameters(response.data.categories[0].parameters || []);
      }
    } catch (error) {
      console.error('Error loading IQC template:', error);
      alert('Failed to load IQC template. Please check backend connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setParameters(category.parameters || []);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'NTF': 'bg-green-100 text-green-800 border-green-300',
      'Low': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Medium': 'bg-orange-100 text-orange-800 border-orange-300',
      'High': 'bg-red-100 text-red-800 border-red-300',
      'Critical_L1': 'bg-purple-100 text-purple-800 border-purple-300',
      'Critical_L4': 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      'NTF': '✅',
      'Low': '🟡',
      'Medium': '🟠',
      'High': '🔴',
      'Critical_L1': '🚨',
      'Critical_L4': '🚨'
    };
    return icons[severity] || '•';
  };

  // Add new parameter
  const addParameter = () => {
    const newParam = {
      parameter_id: `param_${Date.now()}`,
      name: 'New Parameter',
      category: selectedCategory.name,
      input_type: 'radio',
      is_required: true,
      order: parameters.length + 1,
      options: [
        { value: 'Good Condition', severity: 'NTF', triggers_parts: [] }
      ]
    };
    
    const updatedParams = [...parameters, newParam];
    setParameters(updatedParams);
    
    // Update category in state
    const updatedCategories = categories.map(cat => 
      cat.category_id === selectedCategory.category_id 
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
    setEditingParam(newParam.parameter_id);
  };

  // Update parameter field
  const updateParameter = (paramId, field, value) => {
    const updatedParams = parameters.map(p =>
      p.parameter_id === paramId ? { ...p, [field]: value } : p
    );
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Delete parameter
  const deleteParameter = (paramId) => {
    if (!window.confirm('Are you sure you want to delete this parameter?')) return;
    
    const updatedParams = parameters.filter(p => p.parameter_id !== paramId);
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Add new option to parameter
  const addOption = (paramId) => {
    const updatedParams = parameters.map(p => {
      if (p.parameter_id === paramId) {
        const newOption = {
          value: 'New Option',
          severity: 'Low',
          triggers_parts: []
        };
        return { ...p, options: [...p.options, newOption] };
      }
      return p;
    });
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Update option
  const updateOption = (paramId, optionIndex, field, value) => {
    const updatedParams = parameters.map(p => {
      if (p.parameter_id === paramId) {
        const updatedOptions = p.options.map((opt, idx) =>
          idx === optionIndex ? { ...opt, [field]: value } : opt
        );
        return { ...p, options: updatedOptions };
      }
      return p;
    });
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Delete option
  const deleteOption = (paramId, optionIndex) => {
    const updatedParams = parameters.map(p => {
      if (p.parameter_id === paramId) {
        const updatedOptions = p.options.filter((_, idx) => idx !== optionIndex);
        return { ...p, options: updatedOptions };
      }
      return p;
    });
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Add part to option
  const addPart = (paramId, optionIndex, partName) => {
    if (!partName || partName.trim() === '') return;
    
    const updatedParams = parameters.map(p => {
      if (p.parameter_id === paramId) {
        const updatedOptions = p.options.map((opt, idx) => {
          if (idx === optionIndex) {
            const currentParts = opt.triggers_parts || [];
            if (!currentParts.includes(partName.trim())) {
              return { ...opt, triggers_parts: [...currentParts, partName.trim()] };
            }
          }
          return opt;
        });
        return { ...p, options: updatedOptions };
      }
      return p;
    });
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  // Delete part from option
  const deletePart = (paramId, optionIndex, partName) => {
    const updatedParams = parameters.map(p => {
      if (p.parameter_id === paramId) {
        const updatedOptions = p.options.map((opt, idx) => {
          if (idx === optionIndex) {
            const updatedParts = (opt.triggers_parts || []).filter(part => part !== partName);
            return { ...opt, triggers_parts: updatedParts };
          }
          return opt;
        });
        return { ...p, options: updatedOptions };
      }
      return p;
    });
    setParameters(updatedParams);
    
    const updatedCategories = categories.map(cat =>
      cat.category_id === selectedCategory.category_id
        ? { ...cat, parameters: updatedParams }
        : cat
    );
    setCategories(updatedCategories);
    setSelectedCategory({ ...selectedCategory, parameters: updatedParams });
    setHasChanges(true);
  };

  const saveConfiguration = async () => {
    try {
      const token = localStorage.getItem('token');
      const updatedTemplate = {
        ...template,
        categories: categories
      };
      
      await axios.put(
        `${API_URL}/api/iqc/templates/${template.template_id}`,
        updatedTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Configuration saved successfully!');
      setHasChanges(false);
      setIsEditing(false);
      setEditingParam(null);
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading IQC Configuration...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
              <Settings className="w-8 h-8 mr-3" />
              IQC Engine Configuration
            </h1>
            <p className="text-gray-600 mt-1">
              View and manage IQC parameters with part mappings • {categories.length} Categories • {parameters.length} Parameters
            </p>
          </div>
          <div className="flex items-center gap-3">
            {template && (
              <div className="text-right mr-4">
                <p className="text-sm text-gray-500">Template: {template.device_os}</p>
                <p className="text-xs text-gray-400">Version {template.version}</p>
              </div>
            )}
            
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Mode
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (hasChanges && !window.confirm('Discard unsaved changes?')) return;
                    setIsEditing(false);
                    setEditingParam(null);
                    setHasChanges(false);
                    loadConfiguration();
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={saveConfiguration}
                  className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: ZOBOX_BLUE }}
                  disabled={!hasChanges}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                  {hasChanges && <span className="ml-2 px-2 py-1 bg-red-500 text-xs rounded-full">*</span>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Category Selector */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            QC Categories ({categories.length})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {categories.map((category, index) => (
              <button
                key={category.category_id}
                onClick={() => handleCategorySelect(category)}
                className={`p-4 rounded-lg font-medium transition-all border-2 ${
                  selectedCategory?.category_id === category.category_id
                    ? 'text-white border-blue-600'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                }`}
                style={selectedCategory?.category_id === category.category_id ? { backgroundColor: ZOBOX_BLUE } : {}}
              >
                <div className="text-2xl mb-1">{category.icon}</div>
                <div className="text-sm font-semibold">{category.name}</div>
                <div className="text-xs mt-1 opacity-75">
                  {category.parameters?.length || 0} params
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Parameters Display */}
        {selectedCategory && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
                  <span className="text-3xl mr-3">{selectedCategory.icon}</span>
                  {selectedCategory.name}
                </h2>
                <p className="text-gray-600 mt-1">
                  {parameters.length} Parameters • Category Order: {selectedCategory.order}
                </p>
              </div>
              {isEditing && (
                <button
                  onClick={addParameter}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Parameter
                </button>
              )}
            </div>

            <div className="space-y-6">
              {parameters.map((param, index) => (
                <div key={param.parameter_id} className={`border-2 rounded-lg p-5 transition-all ${
                  editingParam === param.parameter_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}>
                  {/* Parameter Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {isEditing && editingParam === param.parameter_id ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              value={param.order}
                              onChange={(e) => updateParameter(param.parameter_id, 'order', parseInt(e.target.value))}
                              className="w-16 px-2 py-1 border rounded"
                              min="1"
                            />
                            <input
                              type="text"
                              value={param.name}
                              onChange={(e) => updateParameter(param.parameter_id, 'name', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Parameter name"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <select
                              value={param.input_type}
                              onChange={(e) => updateParameter(param.parameter_id, 'input_type', e.target.value)}
                              className="px-3 py-2 border rounded-lg"
                            >
                              <option value="radio">Radio</option>
                              <option value="checkbox">Checkbox</option>
                              <option value="dropdown">Dropdown</option>
                            </select>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={param.is_required}
                                onChange={(e) => updateParameter(param.parameter_id, 'is_required', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm font-medium">Required</span>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-gray-700">
                            {param.order}. {param.name}
                          </span>
                          {param.is_required && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                              Required
                            </span>
                          )}
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                            {param.input_type}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2">
                        {editingParam !== param.parameter_id ? (
                          <button
                            onClick={() => setEditingParam(param.parameter_id)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                            title="Edit parameter"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setEditingParam(null)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Done editing"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteParameter(param.parameter_id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded"
                          title="Delete parameter"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Options & Results */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Options & Results ({param.options.length}):
                      </p>
                      {isEditing && editingParam === param.parameter_id && (
                        <button
                          onClick={() => addOption(param.parameter_id)}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Option
                        </button>
                      )}
                    </div>
                    
                    {param.options.map((option, optIdx) => (
                      <div 
                        key={optIdx} 
                        className={`p-4 rounded-lg border-2 ${getSeverityColor(option.severity)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {isEditing && editingParam === param.parameter_id ? (
                              <div className="space-y-3">
                                {/* Editable Option Value */}
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">{getSeverityIcon(option.severity)}</span>
                                  <input
                                    type="text"
                                    value={option.value}
                                    onChange={(e) => updateOption(param.parameter_id, optIdx, 'value', e.target.value)}
                                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Option value"
                                  />
                                </div>
                                
                                {/* Editable Severity */}
                                <div className="flex items-center gap-4">
                                  <label className="text-sm font-medium text-gray-600">Severity:</label>
                                  <select
                                    value={option.severity}
                                    onChange={(e) => updateOption(param.parameter_id, optIdx, 'severity', e.target.value)}
                                    className="px-3 py-1 border rounded-lg text-sm"
                                  >
                                    <option value="NTF">NTF (No Trouble Found)</option>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                    <option value="Critical_L1">Critical_L1</option>
                                    <option value="Critical_L4">Critical_L4</option>
                                  </select>
                                  
                                  {(option.severity === 'Critical_L1' || option.severity === 'Critical_L4') && (
                                    <input
                                      type="text"
                                      value={option.triggers_engineer_level || ''}
                                      onChange={(e) => updateOption(param.parameter_id, optIdx, 'triggers_engineer_level', e.target.value)}
                                      placeholder="Engineer Level (L1/L4)"
                                      className="px-3 py-1 border rounded-lg text-sm w-32"
                                    />
                                  )}
                                </div>
                                
                                {/* Editable Parts */}
                                <div>
                                  <label className="text-sm font-medium text-gray-600 mb-2 block">Parts Required:</label>
                                  <div className="flex flex-wrap gap-2">
                                    {(option.triggers_parts || []).map((part, partIdx) => (
                                      <span 
                                        key={partIdx}
                                        className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-medium flex items-center gap-1"
                                      >
                                        {part}
                                        <button
                                          onClick={() => deletePart(param.parameter_id, optIdx, part)}
                                          className="text-red-600 hover:text-red-800 ml-1"
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))}
                                    <input
                                      type="text"
                                      placeholder="Add part name"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          addPart(param.parameter_id, optIdx, e.target.value);
                                          e.target.value = '';
                                        }
                                      }}
                                      className="px-3 py-1 border border-dashed border-gray-400 rounded text-xs w-32"
                                    />
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-lg">{getSeverityIcon(option.severity)}</span>
                                  <span className="font-semibold text-gray-800">{option.value}</span>
                                </div>
                                
                                <div className="flex flex-wrap gap-4 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-600">Severity: </span>
                                    <span className="font-semibold">{option.severity}</span>
                                  </div>
                                  
                                  {option.triggers_parts && option.triggers_parts.length > 0 && (
                                    <div className="flex-1">
                                      <span className="font-medium text-gray-600">Parts Required: </span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {option.triggers_parts.map((part, partIdx) => (
                                          <span 
                                            key={partIdx}
                                            className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-medium"
                                          >
                                            {part}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {option.triggers_engineer_level && (
                                    <div>
                                      <span className="font-medium text-gray-600">Engineer Level: </span>
                                      <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded">
                                        {option.triggers_engineer_level}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                          
                          {isEditing && editingParam === param.parameter_id && (
                            <button
                              onClick={() => deleteOption(param.parameter_id, optIdx)}
                              className="ml-2 p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Delete option"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {parameters.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No parameters found</p>
                <p className="text-sm mt-2">This category doesn't have any parameters configured</p>
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="text-3xl mb-1">✅</div>
            <div className="text-sm text-gray-600">NTF Options</div>
            <div className="text-2xl font-bold text-green-700">51</div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="text-3xl mb-1">🟡</div>
            <div className="text-sm text-gray-600">Low Severity</div>
            <div className="text-2xl font-bold text-yellow-700">34</div>
          </div>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
            <div className="text-3xl mb-1">🟠</div>
            <div className="text-sm text-gray-600">Medium Severity</div>
            <div className="text-2xl font-bold text-orange-700">39</div>
          </div>
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="text-3xl mb-1">🔴</div>
            <div className="text-sm text-gray-600">High + Critical</div>
            <div className="text-2xl font-bold text-red-700">25</div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            IQC Engine Information
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>• This screen displays all IQC parameters loaded from the database template</p>
            <p>• Each parameter shows options with severity levels and part mappings</p>
            <p>• <strong>NTF (No Trouble Found)</strong>: Device is in good condition for this parameter</p>
            <p>• <strong>Low/Medium/High</strong>: Parts required based on severity</p>
            <p>• <strong>Critical_L1/L4</strong>: Requires escalation to specific engineer levels</p>
            <p>• Parts shown are automatically triggered when the option is selected during IQC</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IQCEngine;